import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../services/api';
import { CommissionPeriod, CommissionPlan } from '../types';
import clsx from 'clsx';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  calculated: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  paid: 'bg-[var(--bg-tertiary)] text-[var(--text-primary)]',
};

export default function Commissions() {
  const [activeTab, setActiveTab] = useState<'periods' | 'plans'>('periods');
  const queryClient = useQueryClient();

  const { data: periods, isLoading: loadingPeriods } = useQuery({
    queryKey: ['commission-periods'],
    queryFn: async () => {
      const response = await api.get('/commissions/periods');
      return response.data.data as CommissionPeriod[];
    },
  });

  const { data: plans, isLoading: loadingPlans } = useQuery({
    queryKey: ['commission-plans'],
    queryFn: async () => {
      const response = await api.get('/commissions/plans');
      return response.data.data as CommissionPlan[];
    },
  });

  const calculatePeriod = useMutation({
    mutationFn: async (periodId: string) => {
      await api.post(`/commissions/periods/${periodId}/calculate`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission-periods'] });
      toast.success('Commissions calculated');
    },
    onError: () => {
      toast.error('Failed to calculate commissions');
    },
  });

  const approvePeriod = useMutation({
    mutationFn: async (periodId: string) => {
      await api.post(`/commissions/periods/${periodId}/approve`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission-periods'] });
      toast.success('Period approved');
    },
    onError: () => {
      toast.error('Failed to approve period');
    },
  });

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">Commissions</h1>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            Manage commission periods and plans.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6 border-b border-[var(--border-color)]">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('periods')}
            className={clsx(
              activeTab === 'periods'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:border-[var(--border-color)]',
              'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm'
            )}
          >
            Commission Periods
          </button>
          <button
            onClick={() => setActiveTab('plans')}
            className={clsx(
              activeTab === 'plans'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:border-[var(--border-color)]',
              'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm'
            )}
          >
            Commission Plans
          </button>
        </nav>
      </div>

      {/* Periods Tab */}
      {activeTab === 'periods' && (
        <div className="mt-6">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-[var(--bg-secondary)]">
                <tr>
                  <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-[var(--text-primary)]">
                    Period
                  </th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                    Status
                  </th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                    Total Commissions
                  </th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                    Calculated At
                  </th>
                  <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-[var(--bg-secondary)]">
                {loadingPeriods ? (
                  <tr>
                    <td colSpan={5} className="py-10 text-center text-[var(--text-muted)]">
                      Loading...
                    </td>
                  </tr>
                ) : periods?.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-10 text-center text-[var(--text-muted)]">
                      No commission periods found
                    </td>
                  </tr>
                ) : (
                  periods?.map((period) => (
                    <tr key={period.id}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-[var(--text-primary)]">
                        {new Date(period.periodStart).toLocaleDateString()} -{' '}
                        {new Date(period.periodEnd).toLocaleDateString()}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        <span
                          className={clsx(
                            'badge',
                            statusColors[period.status as keyof typeof statusColors]
                          )}
                        >
                          {period.status}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-primary)]">
                        ${period.totalCommissions.toLocaleString()}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-muted)]">
                        {period.calculatedAt
                          ? new Date(period.calculatedAt).toLocaleString()
                          : '-'}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                        {period.status === 'pending' && (
                          <button
                            onClick={() => calculatePeriod.mutate(period.id)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            Calculate
                          </button>
                        )}
                        {period.status === 'calculated' && (
                          <button
                            onClick={() => approvePeriod.mutate(period.id)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Approve
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Plans Tab */}
      {activeTab === 'plans' && (
        <div className="mt-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {loadingPlans ? (
              <div className="col-span-full text-center text-[var(--text-muted)]">Loading...</div>
            ) : plans?.length === 0 ? (
              <div className="col-span-full text-center text-[var(--text-muted)]">
                No commission plans found
              </div>
            ) : (
              plans?.map((plan) => (
                <div
                  key={plan.id}
                  className="bg-[var(--bg-secondary)] shadow rounded-lg p-6"
                >
                  <div className="flex justify-between items-start">
                    <h3 className="text-lg font-medium text-[var(--text-primary)]">{plan.name}</h3>
                    {plan.isDefault && (
                      <span className="badge badge-info">Default</span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-[var(--text-muted)] capitalize">{plan.type}</p>

                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-[var(--text-secondary)]">Tiers</h4>
                    <ul className="mt-2 space-y-1">
                      {plan.tiers.map((tier, index) => (
                        <li key={index} className="text-sm text-[var(--text-secondary)]">
                          {plan.type === 'revshare' && (
                            <>Threshold ${tier.threshold}: {tier.rate}%</>
                          )}
                          {plan.type === 'cpa' && (
                            <>Threshold {tier.threshold}: ${tier.cpaAmount} CPA</>
                          )}
                          {plan.type === 'lot_rebate' && (
                            <>Threshold {tier.threshold} lots: ${tier.rebatePerLot}/lot</>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {plan.negativeCarryover && (
                    <p className="mt-4 text-xs text-[var(--text-muted)]">
                      * Negative carryover enabled
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
